import { observable, runInAction, makeObservable, computed } from 'mobx';
import { fetchPaymentConfig } from '../Services/Api/_HumanWritten/PaymentService';
import type { PaymentProvider, PaymentProviderCapabilities } from '../Services/Api/_HumanWritten/PaymentService';

interface IPaymentStore {
	provider: PaymentProvider;
	publishableKey: string;
	accountId: string;
	currency: string;
	currencyCode: string;
	currencyLocale: string;
	applePayFeePercentage: number;
	googlePayFeePercentage: number;
	otherCardFeePercentage: number;
	capabilities: PaymentProviderCapabilities;
	isSandbox: boolean;
	isReady: boolean;
	hasError: boolean;

	retrieveAsync(refresh?: boolean): Promise<void>;
}

const DEFAULT_CAPABILITIES: PaymentProviderCapabilities = {
	supportsRefunds: false,
	supportsSavedCards: false,
	supportsTerminalPayments: false,
	supportsDynamicSurcharge: false,
	supportsApplePay: false,
	supportsGooglePay: false,
};

export class PaymentStore implements IPaymentStore {
	/**
	 * The active payment provider (e.g. 'stripe', 'square'). Drives which provider SDK is
	 * loaded and which payment form is rendered. In Phase A this is always 'stripe'.
	 */
	@observable
	private _provider: PaymentProvider = 'STRIPE';

	/**
	 * The active provider's public key (Stripe publishable key, Square application ID, etc.).
	 */
	@observable
	private _publishableKey: string = '';

	/**
	 * The active provider's account/location identifier (Stripe connected account, Square location ID).
	 */
	@observable
	private _accountId: string = '';

	/**
	 * The currency to pay in.
	 */
	@observable
	private _currency: string = 'AUD';

	/**
	 * The currency locale (used to display the currency symbol).
	 */
	@observable
	private _currencyCode: string = 'AUD';

	/**
	 * The currency locale (used to display the currency symbol).
	 */
	@observable
	private _currencyLocale: string = 'en-AU';

	/**
	 * Surcharge percentages by payment method. Exposed to UI so wallet sheets (Apple Pay /
	 * Google Pay) can display the surcharge-inclusive total — the native wallet modal uses
	 * the amount configured on the provider SDK at open time, before the server has a chance
	 * to recompute.
	 */
	@observable
	private _applePayFeePercentage: number = 0;

	@observable
	private _googlePayFeePercentage: number = 0;

	@observable
	private _otherCardFeePercentage: number = 0;

	/**
	 * The active provider's capability flags. UI components read these to hide / disable
	 * features the active provider doesn't support (e.g. saved cards, terminal payments).
	 */
	@observable
	private _capabilities: PaymentProviderCapabilities = DEFAULT_CAPABILITIES;

	/**
	 * True when the payment provider is running in sandbox/test mode.
	 */
	@observable
	private _isSandbox: boolean = false;

	/**
	 * True once the config has been fetched. Observable so components can react
	 * to it and wait before initialising payment provider SDKs.
	 */
	@observable
	private _isReady: boolean = false;

	/**
	 * True means the config has been fetched, false otherwise.
	 */
	private _fetched: boolean = false;

	/**
	 * Set when the config fetch failed. Without this a failure was indistinguishable from "still
	 * loading" — `_isReady` simply stayed false forever and every payment surface showed a spinner
	 * with nothing to retry it, because the only caller runs its effect once on mount.
	 */
	@observable
	private _hasError: boolean = false;

	constructor() {
		makeObservable(this);
	}

	public async retrieveAsync(refresh = false) {
		if (refresh) {
			this._fetched = false;
		}
		if (this._fetched) {
			return;
		}

		runInAction(() => {
			this._hasError = false;
		});

		// Fetch unified config from the backend's get-payment-config endpoint.
		let config: Awaited<ReturnType<typeof fetchPaymentConfig>>;
		try {
			config = await fetchPaymentConfig();
		} catch (error) {
			// Leave _fetched false so a retry actually re-fetches, and record the failure so callers
			// can offer that retry. Swallowing this was what turned one failed request into a
			// permanent spinner.
			console.error('Could not load payment configuration', error);
			runInAction(() => {
				this._hasError = true;
			});
			return;
		}

		runInAction(() => {
			this._provider = config.provider;
			this._publishableKey = config.publicKey;
			this._accountId = config.accountId ?? '';
			this._currency = config.currency;
			this._currencyCode = config.currencyCode;
			this._currencyLocale = config.currencyLocale;
			this._applePayFeePercentage = config.applePayFeePercentage ?? 0;
			this._googlePayFeePercentage = config.googlePayFeePercentage ?? 0;
			this._otherCardFeePercentage = config.otherCardFeePercentage ?? 0;
			this._capabilities = config.capabilities ?? DEFAULT_CAPABILITIES;
			this._isSandbox = config.isSandbox ?? false;
			this._fetched = true;
			this._isReady = true;
		});

		// Preemptively (1) load the active provider's chunk and (2) kick off its SDK download
		// so the payment form renders instantly the first time the user reaches the payment tab.
		//
		// Two things have to happen before <PaymentElement> can render:
		//   a) The lazy chunk (StripeProvider.tsx) has to be downloaded — webpack caches this
		//      via the dynamic import below.
		//   b) Stripe.js itself has to be loaded from Stripe's CDN via loadStripe() — Stripe
		//      caches this via the module-level stripePromise inside StripeProvider.tsx.
		//
		// Without this preload, on the FIRST visit to the payment tab the user sees an empty
		// form while (a) and (b) sequentially run. Subsequent visits are fast because both
		// caches are warm. Doing both during app boot makes the first visit equally fast.
		if (config.provider === 'STRIPE') {
			import('Views/Components/_HumanWritten/Payment/StripeProvider')
				.then(module => module.initializeStripe())
				.catch(() => {
					// Swallow — the React.lazy() in PaymentProviderWrapper will surface the
					// actual error if the chunk genuinely can't be loaded when the form mounts.
				});
		}
		if (config.provider === 'SQUARE') {
			import('Views/Components/_HumanWritten/Payment/SquareProvider')
				.then(module => module.initializeSquare())
				.catch(() => {
					// Same fall-through as Stripe — the lazy chunk's Suspense fallback will
					// surface a real loading error if this fails permanently.
				});
		}
	}

	/** True when the last config fetch failed. Pairs with `retrieveAsync(true)` to retry. */
	@computed
	public get hasError() {
		return this._hasError;
	}

	public get provider() {
		return this._provider;
	}

	public get publishableKey() {
		return this._publishableKey;
	}

	public get accountId() {
		return this._accountId;
	}

	public get currency() {
		return this._currency;
	}

	public get currencyCode() {
		return this._currencyCode;
	}

	public get currencyLocale() {
		return this._currencyLocale;
	}

	public get applePayFeePercentage() {
		return this._applePayFeePercentage;
	}

	public get googlePayFeePercentage() {
		return this._googlePayFeePercentage;
	}

	public get otherCardFeePercentage() {
		return this._otherCardFeePercentage;
	}

	public get isSandbox() {
		return this._isSandbox;
	}

	public get capabilities() {
		return this._capabilities;
	}

	public get isReady() {
		return this._isReady;
	}
}

const paymentStore: IPaymentStore = new PaymentStore();
export default paymentStore;
